import { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const DEFAULT_CAM = _isMobile ? { x: 0.63, y: 0.70, z: 0.63 } : { x: 0.56, y: 0.44, z: 0.56 };
const _mobileTargetY = _isMobile ? -0.02 : -0.02;
const SCALE = 0.008;
const SEGMENTS = 64;

const RoundCushion3DPreview = ({
  diameter = 50, thickness = 5, fabricColor, fabricImageUrl, fabricZoom = 2, showZip = true, rawDiameter = 0, rawThickness, unit = 'cm',
  activeField = null, pipingOption = 'none', pipingColor = null, pipingImageUrl = null
}) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const zipGroupRef = useRef(null);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);
  const sceneRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ diameter: null, thickness: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const activeFieldRef = useRef(activeField);
  const groundRef = useRef(null);
  const diamLineRef = useRef(null);
  
  const color = fabricColor || '#5a8a5a';
  
  const fabricImageUrlRef = useRef(fabricImageUrl);
  const colorRef = useRef(color);
  const fabricZoomRef = useRef(fabricZoom);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { fabricImageUrlRef.current = fabricImageUrl; colorRef.current = color; }, [fabricImageUrl, color]);
  useEffect(() => { fabricZoomRef.current = fabricZoom; }, [fabricZoom]);

  const dims = useMemo(() => {
    const sD = Math.max(diameter * SCALE, 0.1);
    const sT = Math.max(thickness * SCALE, 0.03);
    return { d: sD, t: sT };
  }, [diameter, thickness]);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, -0.02, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, _mobileTargetY, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    // Shadow ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    ground.receiveShadow = true;
    scene.add(ground);
    groundRef.current = ground;

    // Build cushion using lathe for rounded edge profile
    const { d, t } = dims;
    const cushionGeo = createRoundCushionGeometry(d / 2, t);
    const cushionMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(color),
      roughness: 0.85,
      metalness: 0.02,
    });
    const cushion = new THREE.Mesh(cushionGeo, cushionMat);
    cushion.castShadow = true;
    scene.add(cushion);
    cushionRef.current = cushion;
    currentDimsRef.current = { ...dims };
    targetDimsRef.current = { ...dims };

    // Zip group: track + teeth + pull along the bottom diameter
    const zipMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipTrack = new THREE.Mesh(new THREE.BoxGeometry(1, 0.008, 0.012), zipMat);
    scene.add(zipTrack);
    const zipTeeth = [];
    for (let i = 0; i < 48; i++) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.008, 0.008), zipMat);
      scene.add(tooth);
      zipTeeth.push(tooth);
    }
    const zipPull = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.007, 0.008), zipMat);
    scene.add(zipPull);
    zipGroupRef.current = { track: zipTrack, teeth: zipTeeth, pull: zipPull };

    // 3D diameter dimension line (dashes + arrow cones, properly occluded by cushion)
    const dimLineMat = new THREE.MeshBasicMaterial({ color: 0xcccccc });
    const diamDashes = [];
    for (let i = 0; i < 14; i++) {
      const dash = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.008), dimLineMat);
      scene.add(dash);
      diamDashes.push(dash);
    }
    const dimArrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);
    const diamArrowFront = new THREE.Mesh(dimArrowGeo, dimLineMat);
    const diamArrowBack = new THREE.Mesh(dimArrowGeo, dimLineMat);
    scene.add(diamArrowFront);
    scene.add(diamArrowBack);
    diamLineRef.current = { dashes: diamDashes, arrowFront: diamArrowFront, arrowBack: diamArrowBack, mat: dimLineMat };

    // Piping rings (top + bottom circular edges)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;
    sceneRef.current = scene;

    // Animation loop
    const targetCamPos = new THREE.Vector3();
    const zeroVec = new THREE.Vector3(0, 0, 0);

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = diamLineRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });

              const __zg = zipGroupRef.current;
        if (__zg) {
          __zg.visible = false;
        }
}
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) { renderer.render(scene, camera); return; }

      // Lerp dimensions toward target
      const dimChanged = Math.abs(cd.d - td.d) > 0.0001 || Math.abs(cd.t - td.t) > 0.0001;
      if (dimChanged) {
        cd.d += (td.d - cd.d) * 0.1;
        cd.t += (td.t - cd.t) * 0.1;
        cushion.geometry.dispose();
        cushion.geometry = createRoundCushionGeometry(cd.d / 2, cd.t);
        applyFabricTexture(cushion, fabricImageUrlRef.current, colorRef.current, fabricZoomRef.current);
      }
      const r = cd.d / 2;

      // Ground
      ground.position.y = -cd.t / 2 - 0.002;

      // Piping Y tracking — follow bevel midpoint during animation
      if (pipingTopRef.current && pipingTopRef.current.visible) {
        const bvl = Math.min(cd.t * 0.15, (cd.d / 2) * 0.08, 0.012);
        const pY = cd.t / 2 - bvl + Math.SQRT1_2 * bvl;
        pipingTopRef.current.position.y = pY;
        pipingBottomRef.current.position.y = -pY;
      }

      // Zip on bottom, along X-axis diameter
      if (zipGroupRef.current) {
        const { track, teeth, pull } = zipGroupRef.current;
        track.visible = showZip;
        teeth.forEach(t => t.visible = showZip);
        pull.visible = showZip;
        if (showZip) {
          const bottomY = -cd.t / 2 - 0.005;
          const zipLen = cd.d * 0.88;

          track.position.set(0, bottomY, 0);
          track.rotation.set(0, 0, 0);
          track.scale.set(zipLen, 1, 1);

          const halfZip = zipLen / 2;
          const spacing = zipLen / teeth.length;
          for (let i = 0; i < teeth.length; i++) {
            const tx = -halfZip + (i + 0.5) * spacing;
            const yOff = (i % 2 === 0 ? 1 : -1) * 0.010;
            teeth[i].position.set(tx, bottomY + yOff, 0);
            teeth[i].rotation.set(0, 0, 0);
          }

          pull.position.set(halfZip + 0.01, bottomY - 0.008, 0);
        }
      }

      // 3D diameter dimension line positioning
      if (diamLineRef.current && diamLineRef.current.dashes) {
        const { dashes, arrowFront, arrowBack } = diamLineRef.current;
        const topY = cd.t / 2 + 0.006;
        for (let i = 0; i < dashes.length; i++) {
          const t = (i + 0.5) / dashes.length;
          const z = -r + cd.d * t;
          dashes[i].position.set(0, topY, z);
        }
        const arrowHalf = 0.0075;
        arrowFront.position.set(0, topY, -r + arrowHalf);
        arrowFront.rotation.set(-Math.PI / 2, 0, 0);
        arrowBack.position.set(0, topY, r - arrowHalf);
        arrowBack.rotation.set(Math.PI / 2, 0, 0);
      }

      // Camera auto-fit on dimension change
      if (resetCameraRef.current) {
        const maxDim = cd.d;
        const isMobile = containerWidthRef.current < 500;
        if (isMobile) {
          // Mobile uses an exact, user-tuned camera pose.
          targetCamPos.set(0.4056, 0.5491, 0.6082);
        } else {
          const distMult = 3.0;
          const distBase = 0.3;
          const camScale = 1.5;
          const dist = maxDim * distMult + distBase;
          targetCamPos.set(
            DEFAULT_CAM.x / camScale * dist,
            DEFAULT_CAM.y / camScale * dist,
            DEFAULT_CAM.z / camScale * dist
          );
        }
        camera.position.lerp(targetCamPos, 0.15);
        const targetLookAt = isMobile
          ? new THREE.Vector3(-0.004, -0.013, -0.0037)
          : new THREE.Vector3(0, _mobileTargetY, 0);
        controls.target.lerp(targetLookAt, 0.15);
        if (camera.position.distanceTo(targetCamPos) < 0.02) {
          resetCameraRef.current = false;
          controls.saveState();
        }
      }

      renderer.render(scene, camera);

      // Project dimension labels to 2D + highlight active field
      const af = activeFieldRef.current;
      const YELLOW = 0xffd700;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      // Highlight diameter line
      if (diamLineRef.current && diamLineRef.current.mat) {
        diamLineRef.current.mat.color.set(af === 'diameter' ? YELLOW : 0xcccccc);
      }

      const labels = dimLabelsRef.current;
      if (labels.diameter && labels.thickness) {
        const cRect = container.getBoundingClientRect();
        const project = (wp) => {
          const v = wp.clone().project(camera);
          return { x: (v.x * 0.5 + 0.5) * cRect.width, y: (-v.y * 0.5 + 0.5) * cRect.height, visible: v.z < 1 };
        };

        // Diameter label
        const dP = project(new THREE.Vector3(-0.03, cd.t / 2 + 0.015, 0));
        labels.diameter.style.left = dP.x + 'px';
        labels.diameter.style.top = dP.y + 'px';
        labels.diameter.style.opacity = dP.visible ? '1' : '0';
        labels.diameter.style.backgroundColor = af === 'diameter' ? activeBg : defaultBg;
        labels.diameter.style.color = af === 'diameter' ? activeText : defaultText;

        // Thickness label
        const tP = project(new THREE.Vector3(r + 0.02, 0, 0));
        labels.thickness.style.left = tP.x + 'px';
        labels.thickness.style.top = tP.y + 'px';
        labels.thickness.style.opacity = tP.visible ? '1' : '0';
        labels.thickness.style.backgroundColor = af === 't' ? activeBg : defaultBg;
        labels.thickness.style.color = af === 't' ? activeText : defaultText;
      }
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update target dimensions on prop change
  useEffect(() => {
    if (!targetDimsRef.current) return;
    targetDimsRef.current = { ...dims };
    resetCameraRef.current = true;
  }, [dims]);

  // Update color / texture
  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
  }, [color, fabricImageUrl]);

  // Update texture zoom
  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings (circular — using TubeGeometry around a circle curve)
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    // Determine piping visual props
    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    // Build circular piping path — sit at bevel midpoint (45°)
    const radius = dims.d / 2;
    const bevel = Math.min(dims.t * 0.15, radius * 0.08, 0.012);
    const tubeRadius = 0.003;
    const cos45 = Math.SQRT1_2; // ~0.707
    const sin45 = Math.SQRT1_2;
    // Bevel arc center is at (radius - bevel, ±(height/2 - bevel))
    // 45° point on the arc gives the piping its "riding the corner" look
    const pipingR = radius - bevel + cos45 * bevel;
    const segments = 128;

    // Custom circle curve for TubeGeometry
    class CircleCurve extends THREE.Curve {
      constructor(r) { super(); this.r = r; }
      getPoint(t) {
        const angle = t * Math.PI * 2;
        return new THREE.Vector3(this.r * Math.cos(angle), 0, this.r * Math.sin(angle));
      }
    }

    const curve = new CircleCurve(pipingR);
    const tubeGeo = new THREE.TubeGeometry(curve, segments, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    const pipingY = dims.t / 2 - bevel + sin45 * bevel;
    pt.position.y = pipingY;
    pb.position.y = -pipingY;
  }, [dims, pipingOption, pipingColor, pipingImageUrl, fabricColor, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div
      className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }}
      data-testid="round-cushion-3d-preview"
    >
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="three-canvas-container" />

      {/* Dimension labels */}
      <div
        ref={el => dimLabelsRef.current.diameter = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-diameter"
      >
        {rawDiameter > 0 ? `${unit === 'mm' ? rawDiameter * 10 : rawDiameter} ${unit}` : 'Diameter'}
      </div>
      <div
        ref={el => dimLabelsRef.current.thickness = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-thickness"
      >
        {rawThickness ? `${unit === 'mm' ? rawThickness * 10 : rawThickness} ${unit} thick` : `T ${unit}`}
      </div>

      {/* Zip legend */}
      <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
        <svg width="24" height="6" viewBox="0 0 24 6" className="flex-shrink-0">
          <line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" />
        </svg>
        <span className="font-medium">Zip on bottom</span>
      </div>

      {/* Reset view button */}
      <button
        onClick={handleReset}
        className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer"
        title="Reset view"
        data-testid="reset-view-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 0 1-15.36 6.36"/>
          <path d="M3 12a9 9 0 0 1 15.36-6.36"/>
          <polyline points="21 3 21 9 15 9"/>
          <polyline points="3 21 3 15 9 15"/>
        </svg>
      </button>

      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

function createRoundCushionGeometry(radius, height) {
  // Profile from center-bottom, along bottom, up the side with bevel, across top, back to center
  const bevel = Math.min(height * 0.15, radius * 0.08, 0.012);
  const steps = 8;
  const points = [];

  // Start at bottom center (axis)
  points.push(new THREE.Vector2(0, -height / 2));
  // Bottom flat surface to edge
  points.push(new THREE.Vector2(radius - bevel, -height / 2));
  // Bottom-right bevel
  for (let i = 1; i <= steps; i++) {
    const a = -Math.PI / 2 + (Math.PI / 2) * (i / steps);
    points.push(new THREE.Vector2(
      radius - bevel + Math.cos(a) * bevel,
      -height / 2 + bevel + Math.sin(a) * bevel
    ));
  }
  // Right side up
  points.push(new THREE.Vector2(radius, height / 2 - bevel));
  // Top-right bevel
  for (let i = 1; i <= steps; i++) {
    const a = (Math.PI / 2) * (i / steps);
    points.push(new THREE.Vector2(
      radius - bevel + Math.cos(a) * bevel,
      height / 2 - bevel + Math.sin(a) * bevel
    ));
  }
  // Top flat surface back to center
  points.push(new THREE.Vector2(0, height / 2));

  const geo = new THREE.LatheGeometry(points, SEGMENTS);

  // Fix UVs: consistent texture scale across all faces
  const pos = geo.attributes.position;
  const uv = geo.attributes.uv;
  const halfH = height / 2;

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);

    if (Math.abs(y + halfH) < 0.0005 || Math.abs(y - halfH) < 0.0005) {
      // Flat top/bottom face — planar projection
      const u = (x / radius + 1) / 2;
      const v = (z / radius + 1) / 2;
      uv.setXY(i, u, v);
    } else {
      // Side/bevel — cylindrical UV with scale matching the flat faces
      // Flat faces: 1 UV unit = 2*radius world units
      // Side circumference: 2*PI*radius -> PI UV units for matching scale
      const angle = Math.atan2(z, x);
      const u = (angle + Math.PI) / (2 * Math.PI) * Math.PI;
      const v = (y + halfH) / (2 * radius);
      uv.setXY(i, u, v);
    }
  }
  uv.needsUpdate = true;

  return geo;
}

export default RoundCushion3DPreview;
